import React, { useEffect, useId, useState } from 'react';
import { Loader2, Plug, X } from 'lucide-react';
import { mcpConnect, mcpListTools, listCredentials } from 'features/agents/api/agentsApi';
import PropertyMultiselectInput from 'shared/components/filters/PropertyMultiselectInput';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  EmptyHint, Field, Notice, SecretInput, SelectField, TextInput, cx, ghostBtnCls, neutralBtnCls, primaryBtnCls, useIssue,
} from 'features/agents/WorkflowEditor/fields/inputs';

/**
 * MCP server picker: the team's saved servers plus "Add MCP server", which
 * opens a small dialog to enter an endpoint and auth, test it and save it as a
 * reusable credential.
 */
export function McpCredentialPicker({ field, value, onChange }) {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const issue = useIssue(field.key);

  const refresh = async () => {
    try {
      const rows = await listCredentials();
      setCreds(rows.filter((r) => r.kind === 'mcp_http'));
    } catch (_) { /* leave the list as it was */ } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <Field label={field.label} fieldKey={field.key}>
      <BrandDropdown
        value={value || ''}
        onChange={(v) => onChange(v || null)}
        options={creds.map((c) => ({ value: c.id, label: c.display_name }))}
        placeholder={loading ? 'Loading' : 'Select an MCP server'}
        triggerClassName={cx('!h-8 !px-2 !py-0 !bg-transparent', issue && '!border-[color:var(--feedback-error)]')}
        footer={(close) => (
          <button
            type="button"
            onClick={() => { close(); setAdding(true); }}
            className="inline-flex h-8 w-full items-center gap-1.5 rounded-md px-2 text-[12px] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)]"
          >
            <Plug className="h-3.5 w-3.5 text-[color:var(--text-muted)]" /> Add MCP server
          </button>
        )}
      />
      {adding ? (
        <AddMcpServerDialog
          onClose={() => setAdding(false)}
          onConnected={(cred) => { setAdding(false); refresh(); onChange(cred.id); }}
        />
      ) : null}
    </Field>
  );
}

function AddMcpServerDialog({ onClose, onConnected }) {
  const titleId = useId();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [authType, setAuthType] = useState('none');
  const [token, setToken] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const connect = async () => {
    setBusy(true); setErr(null);
    try {
      const { credential, tools } = await mcpConnect({
        name: name.trim() || url.trim(),
        url: url.trim(),
        auth_type: authType,
        token: authType === 'bearer' ? token : undefined,
        client_id: authType === 'oauth' ? clientId : undefined,
        client_secret: authType === 'oauth' ? clientSecret : undefined,
      });
      onConnected({ ...credential, tool_count: tools?.length || 0 });
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Could not connect.');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 25%, transparent)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="flex w-full max-w-md flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-token-lg">
        <div className="flex h-11 items-center justify-between border-b border-[color:var(--border-default)] pl-4 pr-2">
          <h2 id={titleId} className="text-[13px] font-semibold text-[color:var(--text-primary)]">Add MCP server</h2>
          <button type="button" onClick={onClose} aria-label="Close" className={cx(ghostBtnCls, 'h-7 w-7 justify-center px-0')}><X className="h-3.5 w-3.5" /></button>
        </div>
        <div className="space-y-3 px-4 py-4">
          <Field label="Name"><TextInput value={name} onChange={setName} placeholder="Linear MCP" drop={false} /></Field>
          <Field label="Endpoint URL"><TextInput value={url} onChange={setUrl} placeholder="https://mcp.example.com" drop={false} /></Field>
          <Field label="Authentication">
            <SelectField value={authType} onChange={setAuthType} options={[{ value: 'none', label: 'None' }, { value: 'bearer', label: 'Bearer token' }, { value: 'oauth', label: 'OAuth' }]} />
          </Field>
          {authType === 'bearer' ? <Field label="Token"><SecretInput value={token} onChange={setToken} /></Field> : null}
          {authType === 'oauth' ? (
            <>
              <Field label="Client ID"><TextInput value={clientId} onChange={setClientId} drop={false} /></Field>
              <Field label="Client secret"><SecretInput value={clientSecret} onChange={setClientSecret} /></Field>
            </>
          ) : null}
          {err ? <Notice tone="error">{err}</Notice> : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[color:var(--border-default)] px-4 py-3">
          <button type="button" onClick={onClose} className={neutralBtnCls}>Cancel</button>
          <button type="button" onClick={connect} disabled={busy || !url.trim()} className={primaryBtnCls}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
            {busy ? 'Connecting' : 'Connect and save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Which of a server's tools the agent may use. Every discovered tool is on the
 * first time they load; unselect any you do not want.
 */
export function McpToolMultiPicker({ field, value, onChange, credentialId }) {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!credentialId) { setTools([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const out = await mcpListTools(credentialId);
        if (!cancelled) setTools(out?.tools || []);
      } catch (_) { if (!cancelled) setTools([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [credentialId]);

  useEffect(() => {
    if (value == null && tools.length) onChange(tools.map((t) => t.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tools]);

  if (!credentialId) {
    return <Field label={field.label}><EmptyHint>Select an MCP server first.</EmptyHint></Field>;
  }
  const selected = Array.isArray(value) ? value : tools.map((t) => t.name);
  return (
    <Field
      label={field.label}
      help="All tools are on by default. Unselect any the agent should not use."
      action={<span className="text-[11px] text-[color:var(--text-muted)]">{loading ? 'Loading' : `${selected.length} of ${tools.length}`}</span>}
    >
      <PropertyMultiselectInput
        value={selected}
        onChange={onChange}
        options={tools.map((t) => ({ value: t.name, label: t.name }))}
        placeholder={loading ? 'Loading tools' : 'All tools'}
      />
    </Field>
  );
}
